import { nameForHoliday, type HolidayNames } from '@/lib/displays/holidayNames';
import { stateSlug } from '@/lib/usStates';
import { formatPostedHours } from '@/lib/displays/postedHours';
import type { DisplaySearchParams } from '@/lib/search/algolia/displaySearchRequest';

export type DiscoveryHoliday = 'Halloween' | 'Christmas';
export type DiscoveryMood = 'all' | 'outing' | 'music';
export const DISCOVERY_PAGE_SIZE = 24;
export const DISCOVERY_RESULT_LIMIT = 1000;

export interface DiscoveryDisplay {
  id: string;
  slug: string;
  name: string;
  city: string;
  state: string;
  imageUrl: string;
  holiday: DiscoveryHoliday;
  coordinates: [number, number] | null;
  hours: string;
}

export interface DiscoveryState {
  holiday: DiscoveryHoliday;
  mood: DiscoveryMood;
  query: string;
  page: number;
  /** "On now" chip (?now=1): only displays lit, or open by posted or listing hours, right now. */
  onNow?: boolean;
}

export const discoveryMoods = (holiday: DiscoveryHoliday) => [
  { id: 'all' as const, name: 'A little of everything' },
  {
    id: 'outing' as const,
    name: holiday === 'Halloween' ? 'Go all out' : 'Neighborhood glow',
  },
  { id: 'music' as const, name: 'Lights with a soundtrack' },
];

export const discoveryOutingTypes = (holiday: DiscoveryHoliday): string[] =>
  holiday === 'Halloween'
    ? ['Commercial', 'Theme Park/Attraction']
    : ['Community/Neighborhood'];

export function readDiscoveryState(
  params: URLSearchParams,
  date = new Date(),
): DiscoveryState {
  const holiday = params.get('holiday')?.toLowerCase();
  const mood = params.get('mood');
  const page = Number(params.get('page'));
  return {
    holiday:
      holiday === 'christmas'
        ? 'Christmas'
        : holiday === 'halloween'
          ? 'Halloween'
          : date.getMonth() >= 10 || date.getMonth() === 0
            ? 'Christmas'
            : 'Halloween',
    mood: mood === 'outing' || mood === 'music' ? mood : 'all',
    query: params.get('q')?.trim() ?? '',
    ...(params.get('now') === '1' ? { onNow: true } : {}),
    page:
      Number.isInteger(page) && page > 0
        ? Math.min(
            page,
            Math.ceil(DISCOVERY_RESULT_LIMIT / DISCOVERY_PAGE_SIZE),
          )
        : 1,
  };
}

/**
 * A search that names a place rather than a display: a US ZIP ("27565",
 * "27565-1234") or "City, ST" / "City, State". The photo list only matches
 * text, so these belong to the map, which looks the place up and sorts by
 * distance. A bare word like "Oxford" stays a text search: it may be a display.
 */
export function isPlaceQuery(query: string): boolean {
  const q = query.trim();
  if (/^\d{5}(?:-\d{4})?$/.test(q)) return true;
  const comma = q.match(/^([a-z][a-z .'-]*),\s*([a-z][a-z .-]*)$/i);
  return Boolean(comma && stateSlug(comma[2]));
}

/** Existing detailed filters still belong to the full map route. */
export function needsFullDisplayMap(params: URLSearchParams): boolean {
  const holidays = params
    .getAll('holiday')
    .flatMap((value) => value.split(','))
    .filter(Boolean);
  return (
    holidays.length > 1 ||
    holidays.some(
      (value) => !['halloween', 'christmas'].includes(value.toLowerCase()),
    ) ||
    [
      'state',
      'city',
      'near',
      'radius',
      'synchronized',
      'charity',
      'feature',
      'view',
      'featured',
      'type',
      'sort',
      'prop-family',
      'prop-product',
      'prop-maker',
    ].some((key) => params.has(key))
  );
}

export function discoverySearchParams(
  state: DiscoveryState,
  onNowIds: number[] = [],
): DisplaySearchParams {
  return {
    query: state.query || '*',
    page: state.page,
    per_page: DISCOVERY_PAGE_SIZE,
    filters: {
      status: 'active',
      holidays: [state.holiday],
      primary_holiday: state.holiday,
      ...(state.mood === 'outing'
        ? { display_type: discoveryOutingTypes(state.holiday) }
        : {}),
      ...(state.mood === 'music' ? { is_synced_to_music: true } : {}),
      ...(state.onNow ? { object_ids: onNowIds } : {}),
    },
  };
}

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';
const list = (value: unknown): string[] =>
  Array.isArray(value) ? value.map(text) : [text(value)];

export function toDiscoveryDisplay(
  hit: unknown,
  holiday: DiscoveryHoliday,
): DiscoveryDisplay | null {
  if (!hit || typeof hit !== 'object') return null;
  const candidate = 'document' in hit ? hit.document : hit;
  if (!candidate || typeof candidate !== 'object') return null;
  const doc = candidate as Record<string, unknown>;
  const id =
    typeof doc.id === 'number'
      ? String(doc.id)
      : text(doc.id) || text(doc.objectID);
  const primaryHoliday = list(doc.holiday_type)[0]?.toLowerCase();
  if (
    !id ||
    primaryHoliday !== holiday.toLowerCase() ||
    (doc.status && doc.status !== 'active')
  )
    return null;
  const geo = doc._geoloc as { lat?: unknown; lng?: unknown } | undefined;
  const latitude = geo?.lat ?? doc.latitude;
  const longitude = geo?.lng ?? doc.longitude;
  const hasCoordinates =
    typeof latitude === 'number' &&
    Number.isFinite(latitude) &&
    Math.abs(latitude) <= 90 &&
    typeof longitude === 'number' &&
    Number.isFinite(longitude) &&
    Math.abs(longitude) <= 180 &&
    (latitude !== 0 || longitude !== 0);
  const imageUrl = text(doc.image_url);
  return {
    id,
    slug: text(doc.slug),
    name: nameForHoliday(
      { name: text(doc.name) || 'Holiday display', holiday_names: doc.holiday_names as HolidayNames | null | undefined },
      holiday,
    ),
    city: text(doc.city),
    state: text(doc.state),
    imageUrl: /^https?:\/\//i.test(imageUrl) ? imageUrl : '',
    holiday,
    coordinates: hasCoordinates ? [longitude, latitude] : null,
    hours: formatPostedHours(text(doc.display_hours)) || text(doc.opens_closes),
  };
}

export const discoveryDisplayPath = (display: DiscoveryDisplay): string =>
  `/display/${encodeURIComponent(display.slug || display.id)}`;

export const discoveryLocation = (display: DiscoveryDisplay): string =>
  [display.city, display.state].filter(Boolean).join(', ') ||
  'Location not listed';
